import axios, { CancelTokenSource } from "axios";
import { ReactElement, useState, useEffect, useRef } from "react";
import Carousel from "react-multi-carousel";
import { useSelector } from "react-redux";
import { T } from "react-translator-component";
import styled from "styled-components";
import {
  Avatar, Box, Button, Container, List, ListItem,
  TextField, Typography
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import profile from "../../../assets/images/profile_img.png";
import { colors } from "../../../common/colors";
import LoadingModal from "../../../components/LoadingModal";
import { convertUserImage } from "../../../util/utils";
import apiServices from "../../services/apiServices";
import { UserEntity } from "../../util/editors";
import SelectedUser from "./SelectedUser";
import dayjs from "../../../util/dayjs";

// Styled components
const AddButton = styled.button`
  margin-left: auto;
  border-color: ${colors.farm_green500};
  border-radius: 20px;
  border-width: 2px;
  width: 40px;
  height: 40px;
  color: ${colors.farm_green500};
`;

interface Props {
  label: string;
  initialUsers: Array<UserEntity>;
  includeNavigator: boolean;
  onAdd: (user: UserEntity) => void;
  onDelete: (user: UserEntity) => void;
  onClose:() => void;
}

export default function AddUserModal ({ label, initialUsers, includeNavigator, onAdd, onDelete, onClose }: Props): ReactElement {
  const scrollRef = useRef<any>(null);
  const { admin } = useSelector((state: any) => state.adminAuth);
  const assignedUsers = initialUsers.filter((user: UserEntity) => user._destroy !== 1)

  const [users, setUsers] = useState<UserEntity[]>([]);
  const [searchTerm, setSearchTerm] = useState<any>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  const handleSearch = async (value: string) => {
    setSearchTerm(value);
    if (!value) {
      setUsers([])
      return
    }

    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Operation canceled due to new request.");
    }
    cancelTokenRef.current = axios.CancelToken.source();

    await searchUsers(value, cancelTokenRef.current.token)
  };

  const searchUsers = async (term: string, token: any) => {
    const cleanedTerm = term.replace(/^[\s\u3000]+/, '');

    try {
      setIsLoading(true);
      const response = await apiServices.searchUsers(cleanedTerm, token, includeNavigator);
      const users = response.users
      const navigators = response.navigators

      const userList = users.concat(navigators)
        .sort((a: any, b: any) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf())

      setUsers(userList.map((user: any) => ({
        userId: user.id,
        userType: user.class_name,
        name: user.name,
        nickname: user.nickname,
        image: user.image,
      })));
    } catch (error) {
      if (axios.isCancel(error)) {
        // pass
      } else {
        console.error("Error fetching search date:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isUserSelected = (searchUser: UserEntity) => {
    return initialUsers.some(
      (user: UserEntity) =>
        user._destroy === 0 &&
        user.userType === searchUser.userType &&
        user.userId === searchUser.userId
    );
  }

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, [initialUsers]);

  return (
    <Box sx={styles.background} onClick={onClose}>
      <Box sx={styles.modalContainer} onClick={(e: any) => e.stopPropagation()}>
        <Container sx={styles.container} maxWidth={false}>
          <LoadingModal visible={isLoading} />

          <Box sx={styles.close}>
            <Button onClick={onClose}>
              <Typography variant="body2" color="textPrimary">
                {T("mng_close")}
              </Typography>
              <KeyboardArrowDownIcon />
            </Button>
          </Box>

          <Box sx={styles.contents}>
            {/* 選択済み送信先 */}
            <Box>
              <Box sx={styles.selected.title}>
                <Typography variant="body2" color="textPrimary">
                  {label}
                </Typography>
                <Typography variant="body2" sx={styles.selected.count}>
                  {assignedUsers.length}
                </Typography>
              </Box>
              <Carousel
                responsive={{
                  superLargeDesktop: {
                    breakpoint: { max: 4000, min: 1024 },
                    items: 5,
                  },
                  desktop: { breakpoint: { max: 1024, min: 768 }, items: 3 },
                  tablet: { breakpoint: { max: 768, min: 464 }, items: 2 },
                  mobile: {
                    breakpoint: { max: 464, min: 0 },
                    items: 2,
                  },
                }}
                swipeable={true}
                draggable={true}
                showDots={false}
                arrows={true}
                infinite={false}
                keyBoardControl={true}
                itemClass="carousel-item-padding"
              >
                {assignedUsers.slice().reverse().map((user: UserEntity) => (
                  <SelectedUser
                    key={user.userId}
                    user={user}
                    onDelete={() => onDelete(user)}
                  />
                ))}
              </Carousel>
            </Box>

            <TextField
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              fullWidth hiddenLabel
            />

            <List sx={styles.searched}>
              {users.map((user: UserEntity) => {
                return (
                  <ListItem key={`${user.userType}_${user.userId}`}>
                    <Avatar
                      src={user.image ? convertUserImage(user.image) : profile}
                      alt={user.nickname}
                    />
                    {user.nickname}
                    {!isUserSelected(user) && (admin.class_name !== user.userType || admin.id !== user.userId) && (
                      <AddButton onClick={() => onAdd(user)}>+</AddButton>
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

const styles = {
  background: {
    position: "fixed",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    zIndex: "9999",
  },
  modalContainer: {
    background: "white",
    borderRadius: "8px",
    width: "100%",
    height: "92%",
    overflowY: "auto",
    zIndex: "9999",
  },
  container: {
    padding: 3,
    paddingTop: 0,
    borderBottom: "1px solid #ddd",
    borderRadius: 1,
    background: colors.farm_white,
    overflow: "auto"
  },
  close: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    "& .MuiButton-root": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      border: "0",
    },
    "& .MuiTypography-root": {
      fontSize: 12,
      color: colors.farm_gray200
    }
  },
  contents: {
    overflowY: "auto"
  },
  selected: {
    title: {
      display: "flex",
      alignItems: "center",
      marginBottom: "4px"
    },
    count: {
      backgroundColor: colors.farm_blue200,
      borderRadius: "4px",
      color: colors.farm_white,
      padding: "2px 8px",
      marginLeft: "4px"
    },
  },
  searched: {
    "& .MuiAvatar-root": {
      marginRight: 1
    }
  }
}
